import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { BookOpen, Plus, Save, Trash2, X, Eye, EyeOff, ArrowUp, ArrowDown, Send, Loader2, Sparkles, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin tab to manage the Connectify Assistant's knowledge base — the sections
// that get stitched into the chatbot's live system prompt. Each entry has a
// heading (idea) and body (information).
const AssistantKbPanel = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, title, body, enabled }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // KB preview — a lightweight sandbox that calls the live assistant endpoint
  // with a fresh session each time so saved KB changes can be tested without
  // leaving the admin panel.
  const [previewSessionId, setPreviewSessionId] = useState(() => `kb-preview-${Date.now()}`);
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewInput, setPreviewInput] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewScrollRef = useRef(null);

  // Admin token is already attached to axios.defaults globally by AppContext.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/assistant-kb`);
      if (r.data?.success) setEntries(r.data.entries || []);
    } catch (e) {
      setError('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setError(''); setEditing({ title: '', body: '', enabled: true, _new: true }); };
  const startEdit = (e) => { setError(''); setEditing({ ...e }); };

  const save = async () => {
    if (!editing) return;
    setError('');
    if (!editing.title.trim() || !editing.body.trim()) {
      setError('Both heading and body are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { title: editing.title.trim(), body: editing.body.trim(), enabled: !!editing.enabled };
      if (editing.id) {
        await axios.put(`${API}/admin/assistant-kb/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/admin/assistant-kb`, payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"? The assistant will stop using this info.`)) return;
    try {
      await axios.delete(`${API}/admin/assistant-kb/${e.id}`);
      await load();
    } catch { setError('Delete failed'); }
  };

  const toggle = async (e) => {
    try {
      await axios.put(`${API}/admin/assistant-kb/${e.id}`, { enabled: !e.enabled });
      await load();
    } catch { setError('Could not update visibility'); }
  };

  const reorder = async (e, dir) => {
    const idx = entries.findIndex((x) => x.id === e.id);
    const target = idx + (dir === 'up' ? -1 : 1);
    if (target < 0 || target >= entries.length) return;
    const neighbour = entries[target];
    try {
      await Promise.all([
        axios.put(`${API}/admin/assistant-kb/${e.id}`, { order: neighbour.order }),
        axios.put(`${API}/admin/assistant-kb/${neighbour.id}`, { order: e.order }),
      ]);
      await load();
    } catch { setError('Could not reorder'); }
  };

  const sendPreview = async () => {
    const text = previewInput.trim();
    if (!text || previewLoading) return;
    const userMsg = { role: 'user', content: text };
    setPreviewMessages((m) => [...m, userMsg]);
    setPreviewInput('');
    setPreviewLoading(true);
    try {
      const r = await axios.post(`${API}/assistant/chat`, {
        message: text,
        session_id: previewSessionId,
        user_email: 'admin-preview',
      });
      const reply = r.data?.response || "Sorry, I couldn't generate a reply.";
      setPreviewMessages((m) => [...m, { role: 'assistant', content: reply }]);
      setTimeout(() => { previewScrollRef.current?.scrollTo({ top: 1e9, behavior: 'smooth' }); }, 50);
    } catch (e) {
      setPreviewMessages((m) => [...m, { role: 'assistant', content: '⚠️ Preview failed. Check the backend logs.' }]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const resetPreview = () => {
    setPreviewMessages([]);
    setPreviewSessionId(`kb-preview-${Date.now()}`);
  };

  return (
    <div className="space-y-6" data-testid="admin-assistant-kb">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-neutral-100">Assistant Knowledge Base</h2>
          <p className="text-sm text-gray-500 dark:text-neutral-400 mt-1">
            Each entry is a section inserted into the Connectify Assistant's system prompt. Changes go live immediately.
          </p>
        </div>
        {!editing && (
          <button
            onClick={startNew}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
            data-testid="admin-kb-new-btn"
          >
            <Plus size={16} /> New Section
          </button>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-xl text-sm">{error}</div>
      )}

      {editing ? (
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100">{editing.id ? 'Edit section' : 'New section'}</h3>
            <button onClick={() => setEditing(null)} className="w-9 h-9 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center" data-testid="admin-kb-close">
              <X size={16} />
            </button>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400 mb-1">Heading (idea)</label>
            <input
              value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              placeholder="e.g. Refund policy"
              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950 focus:border-black outline-none"
              data-testid="admin-kb-title-input"
              maxLength={120}
            />
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-neutral-400 mb-1">Body (information)</label>
            <textarea
              value={editing.body}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              placeholder="Write the full details the assistant should use. Markdown supported — bullet points, bold, headings."
              className="w-full px-4 py-3 border-2 border-gray-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950 focus:border-black outline-none resize-y min-h-[180px] font-mono text-sm"
              data-testid="admin-kb-body-input"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-neutral-300 select-none">
            <input
              type="checkbox"
              checked={!!editing.enabled}
              onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })}
              data-testid="admin-kb-enabled"
            />
            <span>Visible to the assistant</span>
          </label>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-neutral-800">
            <button onClick={() => setEditing(null)} className="px-4 py-2 font-bold text-gray-600 dark:text-neutral-400 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-xl">Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-50"
              data-testid="admin-kb-save"
            >
              <Save size={16} /> {saving ? 'Saving...' : 'Save section'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="p-10 text-center">
            <BookOpen size={28} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 dark:text-neutral-400">No knowledge sections yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-neutral-800">
            {entries.map((e, idx) => (
              <div key={e.id} className={`p-4 flex items-start gap-3 ${!e.enabled ? 'opacity-60' : ''}`} data-testid={`admin-kb-row-${e.id}`}>
                <div className="flex-shrink-0 flex flex-col gap-1">
                  <button onClick={() => reorder(e, 'up')} disabled={idx === 0} className="w-7 h-7 rounded-md bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 disabled:opacity-30 flex items-center justify-center" data-testid={`admin-kb-up-${e.id}`}>
                    <ArrowUp size={12} />
                  </button>
                  <button onClick={() => reorder(e, 'down')} disabled={idx === entries.length - 1} className="w-7 h-7 rounded-md bg-gray-100 dark:bg-neutral-800 hover:bg-gray-200 disabled:opacity-30 flex items-center justify-center" data-testid={`admin-kb-down-${e.id}`}>
                    <ArrowDown size={12} />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 dark:text-neutral-100">{e.title}</div>
                  <p className="text-xs text-gray-500 dark:text-neutral-400 whitespace-pre-wrap line-clamp-3 mt-1">{e.body}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <button onClick={() => toggle(e)} className={`w-9 h-9 rounded-lg flex items-center justify-center ${e.enabled ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600' : 'bg-gray-100 dark:bg-neutral-800 text-gray-500'}`} title={e.enabled ? 'Visible' : 'Hidden'} data-testid={`admin-kb-toggle-${e.id}`}>
                    {e.enabled ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => startEdit(e)} className="px-3 py-2 text-sm font-bold text-gray-700 dark:text-neutral-300 bg-gray-100 dark:bg-neutral-800 rounded-lg hover:bg-gray-200" data-testid={`admin-kb-edit-${e.id}`}>Edit</button>
                  <button onClick={() => remove(e)} className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:bg-red-100 flex items-center justify-center" data-testid={`admin-kb-delete-${e.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assistant preview */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden" data-testid="admin-kb-preview">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-500" />
            <h3 className="font-bold text-gray-900 dark:text-neutral-100">Preview assistant reply</h3>
          </div>
          <button
            onClick={resetPreview}
            className="flex items-center gap-1.5 text-xs font-bold text-gray-500 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-neutral-100"
            data-testid="admin-kb-preview-reset"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
        <div ref={previewScrollRef} className="max-h-80 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50 dark:bg-neutral-950" data-testid="admin-kb-preview-messages">
          {previewMessages.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-neutral-500 italic">
              Ask the assistant anything — this uses the current saved KB and starts with a fresh conversation.
            </p>
          ) : (
            previewMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${m.role === 'user' ? 'bg-black text-white dark:bg-white dark:text-black' : 'bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 text-gray-900 dark:text-neutral-100'}`}>
                  {m.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert prose-p:my-1 prose-li:my-0 prose-ul:my-1">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                </div>
              </div>
            ))
          )}
          {previewLoading ? (
            <div className="flex justify-start">
              <div className="px-4 py-2.5 rounded-2xl bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 text-gray-500 flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Thinking...
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 px-5 py-4 border-t border-gray-100 dark:border-neutral-800">
          <input
            value={previewInput}
            onChange={(e) => setPreviewInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendPreview(); } }}
            placeholder="Ask something a customer might ask..."
            disabled={previewLoading}
            className="flex-1 px-4 py-2.5 border-2 border-gray-200 dark:border-neutral-800 rounded-xl bg-white dark:bg-neutral-950 focus:border-black outline-none text-sm disabled:opacity-60"
            data-testid="admin-kb-preview-input"
          />
          <button
            onClick={sendPreview}
            disabled={previewLoading || !previewInput.trim()}
            className="flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-xl font-bold hover:bg-gray-800 disabled:opacity-40"
            data-testid="admin-kb-preview-send"
          >
            <Send size={14} /> Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssistantKbPanel;
